import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { CalendarClock, Minus, Plus } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { useLanguage } from '../context/LanguageContext';
import { PreorderCheckoutForm, PreorderSuccess } from '../components/PreorderCheckout';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const lineTier = (qty, totalQty, forcedTier, wholesaleAllowed) => {
  if (forcedTier) return forcedTier;
  if (!wholesaleAllowed || totalQty < 12) return 'detail';
  if (totalQty >= 60 || qty >= 12) return 'gros';
  return 'semi_gros';
};

const unitPrice = (p, tier) => {
  const detail = Number(p.price_detail) || 0;
  if (tier === 'vip' || tier === 'gros') return Number(p.price_gros ?? p.price_semi_gros ?? detail) || detail;
  if (tier === 'semi_gros') return Number(p.price_semi_gros ?? detail) || detail;
  return detail;
};

const PreorderPage = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { forcedTier, wholesaleAllowed } = useCart();
  const { t, lang } = useLanguage();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [qtys, setQtys] = useState({});
  const [step, setStep] = useState('list');
  const [result, setResult] = useState(null);

  useEffect(() => {
    axios.get(`${API_URL}/api/preorders/products`)
      .then(r => setProducts(r.data))
      .catch(() => toast.error(t('home_load_error')))
      .finally(() => setLoading(false));
  }, [t]);

  const lines = useMemo(() => {
    const chosen = products.filter(p => (qtys[p.id] || 0) > 0);
    const totalQty = chosen.reduce((s, p) => s + qtys[p.id], 0);
    return chosen.map(p => {
      const tier = lineTier(qtys[p.id], totalQty, forcedTier, wholesaleAllowed);
      const unit = unitPrice(p, tier);
      return { product: p, quantity: qtys[p.id], tier, unit, total: unit * qtys[p.id] };
    });
  }, [products, qtys, forcedTier, wholesaleAllowed]);

  const total = lines.reduce((s, l) => s + l.total, 0);
  const totalQty = lines.reduce((s, l) => s + l.quantity, 0);
  const fmtDate = (d) => (d ? new Date(d).toLocaleDateString(lang === 'fr' ? 'fr-FR' : undefined) : '—');

  const setQty = (p, q) => {
    const max = p.preorder_available || 0;
    setQtys(prev => ({ ...prev, [p.id]: Math.max(0, Math.min(max, q)) }));
  };

  const startCheckout = () => {
    if (authLoading) return;
    if (!user) {
      navigate('/connexion', { state: { from: '/precommande', message: t('preorder_login_required') } });
      return;
    }
    setStep('form');
  };

  const submit = async (form) => {
    const res = await axios.post(`${API_URL}/api/preorders`, {
      items: lines.map(l => ({ product_id: l.product.id, quantity: l.quantity })),
      ...form,
    });
    setResult(res.data);
    setStep('done');
    setQtys({});
    window.location.href = res.data.whatsapp_url;
  };

  if (step === 'done' && result) return <PreorderSuccess order={result} t={t} />;

  return (
    <div className="min-h-screen bg-main pt-24 pb-32" data-testid="preorder-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-10">
          <p className="text-[color:var(--color-primary)] text-sm uppercase tracking-[0.3em] mb-3">{t('preorder_kicker')}</p>
          <h1 className="text-4xl sm:text-5xl text-heading font-light mb-3" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            {t('preorder_title')}
          </h1>
          <p className="text-muted text-sm md:text-base max-w-2xl">{t('preorder_subtitle')}</p>
        </div>

        {step === 'form' ? (
          <PreorderCheckoutForm user={user} lines={lines} total={total} t={t} fmtDate={fmtDate}
            onBack={() => setStep('list')} onSubmit={submit} />
        ) : loading ? (
          <p className="text-muted">...</p>
        ) : products.length === 0 ? (
          <p className="text-muted border border-theme p-10 text-center" data-testid="preorder-empty">{t('preorder_empty')}</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="preorder-products">
            {products.map(p => {
              const q = qtys[p.id] || 0;
              const soldOut = (p.preorder_available || 0) <= 0;
              return (
                <div key={p.id} className="card-surface p-4 flex gap-4" data-testid={`preorder-card-${p.id}`}>
                  <div className="w-24 h-28 bg-surface overflow-hidden flex-shrink-0">
                    <img src={p.image_url} alt={p.name} className="w-full h-full object-cover" loading="lazy"
                      onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1541643600914-78b084683601?w=200'; }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-muted text-[10px] uppercase tracking-[0.15em]">{p.brand}</p>
                    <h3 className="text-heading font-medium truncate">{p.name}</h3>
                    <p className="text-[color:var(--color-primary)] text-xs mt-1 flex items-center gap-1" data-testid={`preorder-card-date-${p.id}`}>
                      <CalendarClock className="w-3.5 h-3.5" /> {t('preorder_expected')} {fmtDate(p.preorder_expected_date)}
                    </p>
                    <p className="text-muted text-xs mt-1">
                      {soldOut ? t('preorder_sold_out') : t('preorder_available', { count: p.preorder_available })}
                    </p>
                    <div className="flex items-center justify-between mt-3">
                      <span className="text-[color:var(--color-primary)] font-semibold">{p.price_detail}€</span>
                      {!soldOut && (
                        <div className="flex items-center border border-theme">
                          <button type="button" onClick={() => setQty(p, q - 1)} className="p-2 text-muted hover:text-heading"
                            data-testid={`preorder-minus-${p.id}`}><Minus className="w-4 h-4" /></button>
                          <input type="number" min="0" max={p.preorder_available} value={q}
                            onChange={(e) => setQty(p, parseInt(e.target.value || 0, 10))}
                            className="w-12 text-center bg-transparent text-heading outline-none"
                            data-testid={`preorder-qty-${p.id}`} />
                          <button type="button" onClick={() => setQty(p, q + 1)} className="p-2 text-muted hover:text-heading"
                            data-testid={`preorder-plus-${p.id}`}><Plus className="w-4 h-4" /></button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {step === 'list' && totalQty > 0 && (
        <div className="fixed bottom-0 inset-x-0 z-40 bg-[color:var(--color-surface)] border-t border-[color:rgb(var(--primary-rgb)/0.30)]" data-testid="preorder-summary-bar">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between gap-4">
            <div>
              <p className="text-zinc-400 text-xs">{t('preorder_items', { count: totalQty })}</p>
              <p className="text-[color:var(--color-primary)] text-xl" style={{ fontFamily: 'Cormorant Garamond, serif' }} data-testid="preorder-total">
                {total.toFixed(2)}€
              </p>
            </div>
            <button type="button" onClick={startCheckout}
              className="bg-[color:var(--color-primary)] text-black px-6 py-3 hover:bg-[color:var(--color-primary-hover)] transition-colors text-sm uppercase tracking-widest"
              data-testid="preorder-continue-btn">
              {t('preorder_continue')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PreorderPage;
